/**
 * AdelantoForm — alta (form completo) + edit (limitado nombre/comentario).
 *
 * Per FWA-PWA-031 G0 D5. State machine se gestiona via StatusTransitionModal,
 * NO via este form (que solo edita nombre + comentario en mode='edit').
 */
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type AdelantoCreatePayload,
  AdelantoCreatePayloadSchema,
  type AdelantoUpdatePayload,
  AdelantoUpdatePayloadSchema,
} from '@sisrep/schemas';
import { useForm } from 'react-hook-form';
import { Button } from './ui/Button.js';
import { Input } from './ui/Input.js';

const TIPO_OPTIONS = [
  { value: 'investidura', label: 'Investidura' },
  { value: 'especialidad', label: 'Especialidad' },
  { value: 'insignia', label: 'Insignia' },
  { value: 'cargo', label: 'Cargo' },
  { value: 'etapa', label: 'Etapa de progresión' },
] as const;

interface AdelantoFormCreateProps {
  mode: 'create';
  initialValues?: Partial<AdelantoCreatePayload>;
  onSubmit: (payload: AdelantoCreatePayload) => void;
  isSubmitting?: boolean;
  serverError?: string;
}

interface AdelantoFormEditProps {
  mode: 'edit';
  initialValues: Partial<AdelantoUpdatePayload> & { tipo_adelanto: string };
  onSubmit: (payload: AdelantoUpdatePayload) => void;
  isSubmitting?: boolean;
  serverError?: string;
}

type AdelantoFormProps = AdelantoFormCreateProps | AdelantoFormEditProps;

export function AdelantoForm(props: AdelantoFormProps) {
  if (props.mode === 'create') return <AdelantoFormCreate {...props} />;
  return <AdelantoFormEdit {...props} />;
}

function AdelantoFormCreate({
  initialValues,
  onSubmit,
  isSubmitting,
  serverError,
}: AdelantoFormCreateProps) {
  // biome-ignore lint/suspicious/noExplicitAny: RHF + zodResolver friction
  const resolver = zodResolver(AdelantoCreatePayloadSchema) as any;
  const {
    register,
    handleSubmit,
    formState: { errors },
    // biome-ignore lint/suspicious/noExplicitAny: see above
  } = useForm<any>({
    resolver,
    mode: 'onBlur',
    defaultValues: { tipo_adelanto: 'insignia', ...initialValues },
  });

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data as AdelantoCreatePayload))}
      className="space-y-4"
    >
      {serverError && (
        <div
          className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <Input
        label="Nina entity_id (ULID)"
        placeholder="01HY7Q..."
        {...register('nina_entity_id')}
        error={errors.nina_entity_id?.message as string | undefined}
        hint="V1: paste el ULID de la niña. V2: autocomplete por nombre."
      />

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">Tipo de adelanto</span>
        <select
          className="px-3 py-2 border border-gray-300 rounded-md"
          {...register('tipo_adelanto')}
        >
          {TIPO_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </label>

      <Input
        label="Nombre del adelanto"
        placeholder="Insignia naturalista"
        {...register('nombre_adelanto')}
        error={errors.nombre_adelanto?.message as string | undefined}
      />

      <Input
        label="Etapa anterior (opcional)"
        {...register('etapa_anterior')}
        error={errors.etapa_anterior?.message as string | undefined}
      />

      <Input
        label="Etapa nueva (opcional)"
        {...register('etapa_nueva')}
        error={errors.etapa_nueva?.message as string | undefined}
      />

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">Comentario (opcional, máx 500)</span>
        <p className="text-xs text-amber-700">
          Puede contener info contextual de la niña — no incluyas nombres ni datos sensibles.
        </p>
        <textarea
          className="px-3 py-2 border border-gray-300 rounded-md"
          rows={3}
          maxLength={500}
          {...register('comentario')}
        />
      </label>

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando…' : 'Crear adelanto (borrador)'}
        </Button>
      </div>
    </form>
  );
}

function AdelantoFormEdit({
  initialValues,
  onSubmit,
  isSubmitting,
  serverError,
}: AdelantoFormEditProps) {
  // biome-ignore lint/suspicious/noExplicitAny: same workaround
  const resolver = zodResolver(AdelantoUpdatePayloadSchema) as any;
  const {
    register,
    handleSubmit,
    formState: { errors },
    // biome-ignore lint/suspicious/noExplicitAny: see above
  } = useForm<any>({
    resolver,
    mode: 'onBlur',
    defaultValues: initialValues,
  });

  return (
    <form
      onSubmit={handleSubmit((data) => onSubmit(data as AdelantoUpdatePayload))}
      className="space-y-4"
    >
      {serverError && (
        <div
          className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <div className="text-sm text-gray-500">
        Tipo: <span className="font-mono">{initialValues.tipo_adelanto}</span> · No se puede
        cambiar. Para otorgar o revocar usa las acciones contextual en la vista de detalle.
      </div>

      <Input
        label="Nombre del adelanto"
        {...register('nombre_adelanto')}
        error={errors.nombre_adelanto?.message as string | undefined}
      />

      <label className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">Comentario (opcional, máx 500)</span>
        <p className="text-xs text-amber-700">
          Puede contener info contextual de la niña — no incluyas nombres ni datos sensibles.
        </p>
        <textarea
          className="px-3 py-2 border border-gray-300 rounded-md"
          rows={3}
          maxLength={500}
          {...register('comentario')}
        />
      </label>

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando…' : 'Guardar cambios'}
        </Button>
      </div>
    </form>
  );
}
